import { useQuery, useQueryClient } from '@tanstack/react-query'

import { canViewModule } from '@/auth/modulePermission'
import { useSessionPermissions } from '@/auth/useCanEditModule'
import { underwritingWorkbooksService, type UnderwritingWorkbookVersion } from '@/services/underwritingWorkbooks'
import { httpStatus } from './httpStatus'

/** The governed RBAC resource every /underwriting-workbooks route checks. */
export const UNDERWRITING_MODELS_RESOURCE = 'underwriting_models'

/** Shown wherever the models would be, for a role that may not read them. */
export const MODELS_NOT_AVAILABLE =
  'Underwriting models are not available to your role. The deal’s own figures, screen and conditions on this tab are unaffected; an administrator can grant model access.'

function refused(error: unknown): boolean {
  return httpStatus(error) === 403
}

function askAgain(query: { state: { error: unknown } }): boolean {
  return !refused(query.state.error)
}

/**
 * The deal's underwriting-model versions, read only by an account allowed to.
 *
 * `underwriting_models` is a governed resource: the role's "*" never confers
 * it, and every refusal is written to the model registry's audit chain. The
 * Step-1 analyst has no grant, so the tab asked on every view — three times
 * with the app's retries, again on every revisit and window focus — and
 * printed "Permission denied" as though the deal had failed to load.
 *
 * The grant is not ours to change. When /auth/me says the account lacks it,
 * nothing is asked. When the profile carries no permissions (an older cached
 * one), the server's 403 is the answer: taken once, never retried, never
 * re-asked on a remount, focus or reconnect while it is cached.
 */
export function useLoanWorkbooks(loanId: string) {
  // The session's own grants: an Entra session (no local profile) is held to
  // /auth/me, and asks nothing until that answers.
  const sessionPermissions = useSessionPermissions()
  const granted = canViewModule(sessionPermissions, UNDERWRITING_MODELS_RESOURCE)
  const queryKey = ['loan-underwriting-workbooks', loanId]
  // A query holding only an error is re-asked by every new observer — each
  // revisit of the tab was another 403. A refusal already cached is the
  // answer; any other failure is still re-asked as before.
  const refusedBefore = refused(useQueryClient().getQueryState(queryKey)?.error)
  const query = useQuery<UnderwritingWorkbookVersion[]>({
    queryKey,
    queryFn: () => underwritingWorkbooksService.listForLoan(loanId),
    enabled: Boolean(loanId) && granted && !refusedBefore,
    staleTime: 30_000,
    retry: (failures, error) => !refused(error) && failures < 2,
    refetchOnMount: askAgain,
    refetchOnWindowFocus: askAgain,
    refetchOnReconnect: askAgain,
  })
  return { query, unavailable: !granted || refused(query.error) }
}
